const SUPPORTED_VIDEO_HOSTS = [
  "youtube.com",
  "www.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "youtu.be",
  "vimeo.com",
  "www.vimeo.com",
  "player.vimeo.com"
];

function urlFor(value) {
  try {
    return new URL(value, window.location.href);
  } catch (_error) {
    return null;
  }
}

function isSupportedVideo(iframe) {
  const url = urlFor(iframe.getAttribute("src"));
  return url && SUPPORTED_VIDEO_HOSTS.includes(url.hostname.toLowerCase());
}

function removeSpacerParagraphs(content) {
  content.querySelectorAll("p").forEach((paragraph) => {
    const hasMeaningfulElement = paragraph.querySelector("a, img, iframe, video, audio, object, embed");
    if (!hasMeaningfulElement && !paragraph.textContent.trim()) paragraph.remove();
  });
}

function externalLinks(content) {
  content.querySelectorAll("a[href]").forEach((link) => {
    const url = urlFor(link.getAttribute("href"));
    if (!url || !["http:", "https:"].includes(url.protocol) || url.origin === window.location.origin) return;

    link.target = "_blank";
    link.rel = "noopener noreferrer";

    if (!link.querySelector(".resource-external-indicator")) {
      const indicator = document.createElement("span");
      indicator.className = "resource-external-indicator";
      indicator.textContent = " (opens in a new tab)";
      link.append(indicator);
    }
  });
}

function directLinkParagraph(element) {
  if (element.tagName !== "P") return false;

  const anchor = element.querySelector(":scope > a[href]");
  if (!anchor || element.children.length !== 1) return false;

  const firstMeaningfulNode = [...element.childNodes].find((node) => {
    return node.nodeType !== Node.TEXT_NODE || node.textContent.trim();
  });

  return firstMeaningfulNode === anchor;
}

function hasLinkDescription(paragraph) {
  const anchor = paragraph.querySelector(":scope > a[href]");
  let sibling = anchor.nextSibling;

  while (sibling) {
    if (sibling.textContent.trim()) return true;
    sibling = sibling.nextSibling;
  }

  return false;
}

// Group adjacent CMS blocks without changing their original order.
function groupConsecutive(elements, className, matches) {
  let group = null;

  elements.forEach((element) => {
    if (!matches(element)) {
      group = null;
      return;
    }

    if (!group) {
      group = document.createElement("div");
      group.className = className;
      element.before(group);
    }

    group.append(element);
  });
}

function enhanceVideos(content) {
  content.querySelectorAll("iframe").forEach((iframe) => {
    if (!isSupportedVideo(iframe) || iframe.closest(".resource-video")) return;

    const precedingElement = iframe.previousElementSibling;
    const titleHeading = precedingElement?.matches("h3, h4") ? precedingElement : null;
    const title = titleHeading?.textContent.trim() || "Resource video";
    const video = document.createElement("figure");
    video.className = "resource-video";

    iframe.before(video);
    video.append(iframe);
    if (titleHeading) {
      titleHeading.classList.add("resource-video__title");
      video.append(titleHeading);
    }

    iframe.loading = "lazy";
    iframe.title = iframe.title || title;
    iframe.allow = "fullscreen; picture-in-picture";
    iframe.allowFullscreen = true;
  });

  groupConsecutive([...content.children], "resource-video-grid", (element) => {
    return element.classList.contains("resource-video");
  });
}

function enhanceLinks(content) {
  const elements = [...content.children];
  elements.filter(directLinkParagraph).forEach((paragraph) => {
    if (hasLinkDescription(paragraph)) paragraph.classList.add("resource-link-card--with-description");
  });
  groupConsecutive(elements, "resource-link-grid", directLinkParagraph);
}

function readableId(text) {
  return text.toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "resource-section";
}

// Preserve the first author-provided ID and suffix later collisions in document order.
function assignHeadingIds(headings) {
  const headingSet = new Set(headings);
  const usedIds = new Set(
    [...document.querySelectorAll("[id]")]
      .filter((element) => !headingSet.has(element))
      .map((element) => element.id)
  );

  headings.forEach((heading) => {
    const baseId = heading.id || readableId(heading.textContent.trim());
    let id = baseId;
    let suffix = 2;
    while (usedIds.has(id)) id = `${baseId}-${suffix++}`;

    heading.id = id;
    usedIds.add(id);
  });
}

function addNavigation(page, headings) {
  if (!headings.length || page.querySelector("[data-resources-navigation]")) return;

  const navigation = document.createElement("nav");
  navigation.className = "resources-page__navigation";
  navigation.dataset.resourcesNavigation = "true";
  navigation.setAttribute("aria-label", "Resource quick links");

  const label = document.createElement("p");
  label.className = "resources-page__navigation-label";
  label.textContent = "Quick links";
  navigation.append(label);

  const list = document.createElement("ul");
  list.className = "resources-page__navigation-list";
  headings.forEach((heading) => {
    const item = document.createElement("li");
    if (heading.tagName === "H3") item.className = "resources-page__navigation-item--subgroup";
    const link = document.createElement("a");
    link.href = `#${encodeURIComponent(heading.id)}`;
    link.textContent = heading.textContent.trim();
    item.append(link);
    list.append(item);
  });
  navigation.append(list);

  const header = page.querySelector("[data-resources-header]");
  if (header) {
    // The general resource comes last; keep its introductory paragraphs with the page title.
    const generalContent = [...page.querySelectorAll("[data-resource-content]")].at(-1);
    while (generalContent?.firstElementChild?.matches("p")) {
      header.append(generalContent.firstElementChild);
    }
    header.after(navigation);
  } else {
    page.prepend(navigation);
  }
}

function bootResources() {
  document.querySelectorAll("[data-resources-page]").forEach((page) => {
    if (page.dataset.resourcesEnhanced === "true") return;
    page.dataset.resourcesEnhanced = "true";

    page.querySelectorAll("[data-resource-content]").forEach((content) => {
      removeSpacerParagraphs(content);
      externalLinks(content);
      enhanceVideos(content);
      enhanceLinks(content);
    });

    const headings = [...page.querySelectorAll(
      "[data-resource-content] h2, [data-resource-content] h3:not(.resource-video__title)"
    )];
    assignHeadingIds(headings);
    addNavigation(page, headings);
  });
}

document.addEventListener("DOMContentLoaded", bootResources);
window.addEventListener("pageshow", bootResources);
